import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import {
  socialApi,
  socialFieldErrors,
  type SocialActionResponse,
  type SocialPlatform,
  type SocialPublication,
  type SocialPublicationItem,
} from '@/api/social'
import { SocialPostPreview } from '@/components/admin/social/SocialPostPreview'
import { SocialItemStatusChip, SocialPublicationStatusChip } from '@/components/admin/social/SocialStatusChips'
import {
  canMarkItemSent,
  canRetryItem,
  publicationPlatforms,
  targetLabel,
} from '@/components/admin/social/socialUi'
import { formatDateTimeDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { safeHttpUrl, telegramHtmlToPlainText } from '@/utils/telegramHtml'

const MAX_POST_URL_LENGTH = 512

interface SocialPublicationDialogProps {
  open: boolean
  publicationId: number | null
  onClose: () => void
  /** Шаг изменился — список за диалогом надо перечитать */
  onChanged: (publication: SocialPublication) => void
}

interface Notice {
  severity: 'success' | 'error'
  text: string
}

/**
 * «Подробнее» из журнала: шаги с исходом и ошибками, решения по шагам и
 * ручная публикация (текст без тегов, обложка файлом).
 */
export function SocialPublicationDialog({ open, publicationId, onClose, onChanged }: SocialPublicationDialogProps) {
  const { t } = useTranslation()

  const [publication, setPublication] = useState<SocialPublication | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [toast, setToast] = useState<Notice | null>(null)
  const [downloading, setDownloading] = useState(false)

  const [itemMenuAnchor, setItemMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuItem, setMenuItem] = useState<SocialPublicationItem | null>(null)

  const [retryOpen, setRetryOpen] = useState(false)
  const [retryItem, setRetryItem] = useState<SocialPublicationItem | null>(null)
  const [markOpen, setMarkOpen] = useState(false)
  const [markItem, setMarkItem] = useState<SocialPublicationItem | null>(null)
  const [markUrl, setMarkUrl] = useState('')
  const [markUrlError, setMarkUrlError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Диалог открывается сразу, данные догружаются под скелетоном
  useEffect(() => {
    if (!open || publicationId === null) return

    let ignore = false
    setPublication(null)
    setLoading(true)
    setLoadError(null)
    setNotice(null)
    socialApi
      .getPublication(publicationId)
      .then((data) => {
        if (!ignore) setPublication(data)
      })
      .catch((err) => {
        if (!ignore) setLoadError(displayErrorMessage(getErrorMessage(err), t('admin.social.loadFailed')))
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })

    return () => {
      ignore = true
    }
  }, [open, publicationId, t])

  const handleExited = () => {
    setPublication(null)
    setLoading(false)
    setLoadError(null)
    setNotice(null)
  }

  const applyResult = (response: SocialActionResponse<SocialPublication>) => {
    setPublication(response.data)
    setNotice({ severity: 'success', text: response.message })
    onChanged(response.data)
  }

  const openRetry = (item: SocialPublicationItem) => {
    setRetryItem(item)
    setActionError(null)
    setRetryOpen(true)
  }

  const openMarkSent = (item: SocialPublicationItem) => {
    setMarkItem(item)
    setMarkUrl('')
    setMarkUrlError(null)
    setActionError(null)
    setMarkOpen(true)
  }

  const handleRetry = async () => {
    if (!retryItem) return

    setSubmitting(true)
    setActionError(null)
    try {
      applyResult(await socialApi.retryItem(retryItem.id))
      setRetryOpen(false)
    } catch (err) {
      setActionError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkSent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!markItem) return

    const url = markUrl.trim()
    if (url !== '' && (safeHttpUrl(url) === null || url.length > MAX_POST_URL_LENGTH)) {
      setMarkUrlError(t('admin.social.markSentDialog.urlInvalid'))
      return
    }

    setSubmitting(true)
    setActionError(null)
    setMarkUrlError(null)
    try {
      applyResult(await socialApi.markItemSent(markItem.id, url === '' ? null : url))
      setMarkOpen(false)
    } catch (err) {
      setMarkUrlError(socialFieldErrors(err).url ?? null)
      setActionError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = (platform: SocialPlatform) => {
    if (!publication) return

    // Из буфера Telegram разметку не применит — копируется текст без тегов
    const text =
      platform === 'telegram'
        ? telegramHtmlToPlainText(publication.text_telegram ?? '')
        : (publication.text_vk ?? '')
    if (text.trim() === '') return

    if (typeof navigator.clipboard?.writeText !== 'function') {
      setToast({ severity: 'error', text: t('admin.social.details.copyFailed') })
      return
    }
    navigator.clipboard
      .writeText(text)
      .then(() => setToast({ severity: 'success', text: t('admin.social.details.copied') }))
      .catch(() => setToast({ severity: 'error', text: t('admin.social.details.copyFailed') }))
  }

  const handleDownloadCover = async () => {
    if (!publication) return

    setDownloading(true)
    try {
      const { blob, filename } = await socialApi.downloadCover(publication.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setToast({ severity: 'error', text: displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')) })
    } finally {
      setDownloading(false)
    }
  }

  const renderItem = (item: SocialPublicationItem, current: SocialPublication) => {
    const postUrl = safeHttpUrl(item.url)
    const captchaUrl = item.status === 'needs_captcha' ? safeHttpUrl(item.captcha_img) : null
    const hasActions = canRetryItem(item, current) || canMarkItemSent(item)

    return (
      <Paper key={item.id} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                {targetLabel(item.target, item.social_target_id)}
              </Typography>
              {item.target && (
                <Chip size="small" variant="outlined" label={t(`admin.social.platforms.${item.target.platform}`)} />
              )}
              <SocialItemStatusChip status={item.status} />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {t('admin.social.details.attempts', { count: item.attempts })}
            </Typography>
            {item.status === 'pending' && item.available_at && (
              <Typography variant="body2" color="text.secondary">
                {t('admin.social.details.availableAt', { time: formatDateTimeDMY(item.available_at) })}
              </Typography>
            )}
            {item.sent_at && (
              <Typography variant="body2" color="text.secondary">
                {t('admin.social.details.sentAt', { time: formatDateTimeDMY(item.sent_at) })}
              </Typography>
            )}
            {postUrl && (
              <Link
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ display: 'inline-block', mt: 0.5 }}
              >
                {t('admin.social.details.openPost')}
              </Link>
            )}

            {/* У отправленного шага API хранит последнюю ошибку как историю — человеку она уже не нужна */}
            {item.error_code && item.status !== 'sent' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error.main" sx={{ overflowWrap: 'anywhere' }}>
                  {t('admin.social.details.errorCode', {
                    label: t(`admin.social.errorCodes.${item.error_code}`, { defaultValue: item.error_code }),
                  })}{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    ({item.error_code})
                  </Box>
                </Typography>
                {item.error_details && (
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {item.error_details}
                  </Typography>
                )}
              </Box>
            )}

            {item.status === 'needs_captcha' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('admin.social.details.captchaHint')}
                </Typography>
                {captchaUrl && (
                  <Box
                    component="img"
                    src={captchaUrl}
                    alt={t('admin.social.details.captchaAlt')}
                    referrerPolicy="no-referrer"
                    sx={{
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
            )}
          </Box>

          <IconButton
            size="small"
            aria-label={t('admin.social.details.stepActions')}
            disabled={!hasActions}
            onClick={(event) => {
              setItemMenuAnchor(event.currentTarget)
              setMenuItem(item)
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    )
  }

  const renderContent = (current: SocialPublication) => {
    const newsUrl = safeHttpUrl(current.news?.url)

    return (
      <>
        {notice && (
          <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <SocialPublicationStatusChip status={current.status} />
          <Typography variant="body2" color="text.secondary">
            {t('admin.social.publishAt')}:{' '}
            {current.publish_at ? formatDateTimeDMY(current.publish_at) : t('admin.social.immediately')}
          </Typography>
          {current.approved_at && (
            <Typography variant="body2" color="text.secondary">
              {t('admin.social.details.approvedAt', { time: formatDateTimeDMY(current.approved_at) })}
            </Typography>
          )}
          {current.cancelled_at && (
            <Typography variant="body2" color="text.secondary">
              {t('admin.social.details.cancelledAt', { time: formatDateTimeDMY(current.cancelled_at) })}
            </Typography>
          )}
          {newsUrl && (
            <Link href={newsUrl} target="_blank" rel="noopener noreferrer" variant="body2">
              {t('admin.social.openOnSite')}
            </Link>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t('admin.social.details.steps')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {current.items.map((item) => renderItem(item, current))}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t('admin.social.details.manualTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.social.details.manualHint')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {publicationPlatforms(current).map((platform) => {
            const hasText = Boolean(platform === 'telegram' ? current.text_telegram : current.text_vk)

            return (
              <Paper key={platform} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t(`admin.social.platforms.${platform}`)}
                </Typography>
                <SocialPostPreview platform={platform} publication={current} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    disabled={!hasText}
                    onClick={() => handleCopy(platform)}
                  >
                    {t('admin.social.details.copyText')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    disabled={!current.cover_path || downloading}
                    onClick={handleDownloadCover}
                  >
                    {t('admin.social.details.downloadCover')}
                  </Button>
                </Box>
              </Paper>
            )
          })}
        </Box>
      </>
    )
  }

  const retryTargetLabel = retryItem ? targetLabel(retryItem.target, retryItem.social_target_id) : ''
  const markTargetLabel = markItem ? targetLabel(markItem.target, markItem.social_target_id) : ''

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionProps={{ onExited: handleExited }}>
        <DialogTitle sx={{ pr: 6 }}>
          {publicationId !== null && t('admin.social.details.title', { id: publicationId })}
          {publication?.news && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {publication.news.title}
            </Typography>
          )}
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="text" width="60%" height={28} />
              <Skeleton variant="rounded" height={110} />
              <Skeleton variant="rounded" height={110} />
              <Skeleton variant="text" width="40%" height={28} />
              <Skeleton variant="rounded" height={180} />
            </Box>
          ) : loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : publication ? (
            renderContent(publication)
          ) : null}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={itemMenuAnchor}
        open={Boolean(itemMenuAnchor)}
        onClose={() => setItemMenuAnchor(null)}
        TransitionProps={{ onExited: () => setMenuItem(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuItem && publication && canRetryItem(menuItem, publication) && (
          <MenuItem
            onClick={() => {
              openRetry(menuItem)
              setItemMenuAnchor(null)
            }}
          >
            {t('admin.social.details.retry')}
          </MenuItem>
        )}
        {menuItem && canMarkItemSent(menuItem) && (
          <MenuItem
            onClick={() => {
              openMarkSent(menuItem)
              setItemMenuAnchor(null)
            }}
          >
            {t('admin.social.details.markSent')}
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={retryOpen}
        onClose={() => {
          if (!submitting) setRetryOpen(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('admin.social.retryDialog.title')}</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          {retryItem?.status === 'needs_human' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('admin.social.retryDialog.needsHumanWarning')}
            </Alert>
          )}
          <Typography>{t('admin.social.retryDialog.text', { target: retryTargetLabel })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryOpen(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color={retryItem?.status === 'needs_human' ? 'warning' : 'primary'}
            onClick={handleRetry}
            disabled={submitting || !retryItem}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('admin.social.details.retry')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={markOpen}
        onClose={() => {
          if (!submitting) setMarkOpen(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box
          component="form"
          onSubmit={handleMarkSent}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <DialogTitle>{t('admin.social.markSentDialog.title')}</DialogTitle>
          <DialogContent>
            {actionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {actionError}
              </Alert>
            )}
            <Typography sx={{ mb: 2 }}>{t('admin.social.markSentDialog.text', { target: markTargetLabel })}</Typography>
            <TextField
              fullWidth
              size="small"
              type="url"
              label={`${t('admin.social.markSentDialog.url')} (${t('common.optional')})`}
              value={markUrl}
              onChange={(event) => {
                setMarkUrl(event.target.value)
                setMarkUrlError(null)
              }}
              error={markUrlError !== null}
              helperText={markUrlError ?? undefined}
              slotProps={{ htmlInput: { maxLength: MAX_POST_URL_LENGTH } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMarkOpen(false)} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !markItem}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('admin.social.details.markSent')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  )
}
